import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTaskbar } from '../../contexts/TaskbarContext';
import { useAuth } from '../../contexts/AuthContext';
import TaskbarClock from './TaskbarCalendar';

const GOLD = '#C9A84C';


const ROLE_NAV = {
  sales_rep: [
    { id: 'dash',        icon: '📊', label: 'Dashboard',      route: '/dashboard/sales' },
    { id: 'pipeline',    icon: '🎯', label: 'Lead Pipeline',  route: '/dashboard/sales/pipeline' },
    { id: 'prospects',   icon: '🤖', label: 'AI Prospects',   route: '/dashboard/sales/ai-prospects' },
    { id: 'clients',     icon: '👥', label: 'My Clients',     route: '/dashboard/sales/clients' },
    { id: 'requests',    icon: '📋', label: 'Requests',       route: '/dashboard/sales/requests' },
    { id: 'quotes',      icon: '📝', label: 'Quotes',         route: '/dashboard/sales/quotes' },
    { id: 'commissions', icon: '💰', label: 'Commissions',    route: '/dashboard/sales/commissions' },
    { id: 'payouts',     icon: '💳', label: 'My Payouts',     route: '/dashboard/sales/payouts' },
    { id: 'referrals',   icon: '🔗', label: 'Referral Links', route: '/dashboard/sales/referrals' },
    { id: 'learning',    icon: '🎓', label: 'Learning',       route: '/dashboard/sales/learning' },
  ],
  admin: [
    { id: 'dash',     icon: '📊', label: 'Dashboard',  route: '/dashboard/admin' },
    { id: 'leads',    icon: '🎯', label: 'Leads',      route: '/dashboard/admin/leads' },
    { id: 'users',    icon: '👥', label: 'Users',      route: '/dashboard/admin/users' },
    { id: 'jobs',     icon: '🔨', label: 'Jobs',       route: '/dashboard/admin/jobs' },
    { id: 'reports',  icon: '📈', label: 'Reports',    route: '/dashboard/admin/reports' },
    { id: 'approvals',icon: '✅', label: 'Approvals',  route: '/dashboard/admin/approvals' },
  ],
  client: [
    { id: 'dash',     icon: '🏠', label: 'Dashboard', route: '/dashboard/client' },
    { id: 'projects', icon: '📁', label: 'Projects',  route: '/dashboard/client/projects' },
    { id: 'requests', icon: '📋', label: 'Requests',  route: '/dashboard/client/requests' },
    { id: 'billing',  icon: '💳', label: 'Billing',   route: '/dashboard/client/billing' },
    { id: 'messages', icon: '💬', label: 'Messages',  route: '/dashboard/client/messages' },
  ],
  contractor: [
    { id: 'dash',     icon: '🏠', label: 'Dashboard', route: '/dashboard/contractor' },
    { id: 'jobs',     icon: '🔨', label: 'Jobs',       route: '/dashboard/contractor/jobs' },
    { id: 'projects', icon: '📁', label: 'Projects',  route: '/dashboard/contractor/projects' },
    { id: 'messages', icon: '💬', label: 'Messages',  route: '/dashboard/contractor/messages' },
  ],
};

const PAGE_CONTEXTS = {
  '/dashboard/sales/pipeline':    { icon: '🎯', label: 'Lead Pipeline',  accent: 'rgba(234,179,8,0.2)',  border: 'rgba(234,179,8,0.35)' },
  '/dashboard/sales/ai-prospects':{ icon: '🤖', label: 'AI Prospects',   accent: 'rgba(168,85,247,0.2)', border: 'rgba(168,85,247,0.35)' },
  '/dashboard/sales/clients':     { icon: '👥', label: 'My Clients',     accent: 'rgba(34,197,94,0.2)',  border: 'rgba(34,197,94,0.35)' },
  '/dashboard/admin/leads':       { icon: '🎯', label: 'Admin Leads',    accent: 'rgba(234,179,8,0.2)',  border: 'rgba(234,179,8,0.35)' },
};

function StartButton({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        border: active ? `1px solid ${GOLD}55` : '1px solid transparent',
        background: active ? `rgba(201,168,76,0.15)` : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: 'all 200ms ease',
        boxShadow: active ? `0 0 14px ${GOLD}30` : 'none',
      }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}}
      title="Start Menu"
    >
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <rect x="1" y="1" width="9" height="9" rx="1.5" fill={active ? GOLD : 'rgba(255,255,255,0.65)'} />
        <rect x="12" y="1" width="9" height="9" rx="1.5" fill={active ? GOLD : 'rgba(255,255,255,0.65)'} />
        <rect x="1" y="12" width="9" height="9" rx="1.5" fill={active ? GOLD : 'rgba(255,255,255,0.65)'} />
        <rect x="12" y="12" width="9" height="9" rx="1.5" fill={active ? GOLD : 'rgba(255,255,255,0.65)'} />
      </svg>
    </button>
  );
}

function TaskbarWindowItem({ item, onRestore, onClose }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 10px 5px 8px',
        borderRadius: 8, maxWidth: 160, flexShrink: 0,
        background: hover ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${hover ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)'}`,
        cursor: 'pointer', transition: 'all 180ms ease',
        height: 36,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onRestore(item.id)}
      title={`Restore: ${item.title}`}
    >
      <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon || '📄'}</span>
      <span style={{
        fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.8)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
      }}>
        {item.title}
      </span>
      <button
        onClick={e => { e.stopPropagation(); onClose(item.id); }}
        style={{
          width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0, background: 'transparent',
          border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)',
          transition: 'all 150ms ease',
        }}
        onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.color = '#ff5f57'; e.currentTarget.style.background = 'rgba(255,95,87,0.15)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.background = 'transparent'; }}
        title="Close"
      >
        ✕
      </button>
      <div style={{
        position: 'absolute', bottom: -2, left: '50%', transform: 'translateX(-50%)',
        width: 16, height: 2, borderRadius: 1, background: GOLD,
      }} />
    </div>
  );
}

function StartMenu({ onClose, role, recentItems }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const menuRef = useRef(null);
  const apps = ROLE_NAV[role] || [];

  const filtered = search.trim()
    ? apps.filter(a => a.label.toLowerCase().includes(search.toLowerCase()))
    : apps;

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const go = (route) => { navigate(route); onClose(); };

  const recentFormatted = recentItems
    .filter(r => r.type !== 'lead' || true)
    .slice(0, 6);

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 54, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      />
      <div
        ref={menuRef}
        style={{
          position: 'fixed', bottom: 58, left: 8, zIndex: 55,
          width: 'min(400px, calc(100vw - 24px))',
          background: 'rgba(14,14,22,0.95)',
          backdropFilter: 'blur(28px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 14,
          boxShadow: '0 -8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
          animation: 'sm-slide-up 200ms ease-out both',
        }}
      >
        <style>{`
          @keyframes sm-slide-up {
            from { opacity: 0; transform: translateY(12px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
          .sm-app-btn:hover { background: rgba(255,255,255,0.07) !important; }
          .sm-recent-item:hover { background: rgba(255,255,255,0.05) !important; }
        `}</style>

        {/* Header */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${GOLD}22`, border: `1px solid ${GOLD}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="1" y="1" width="7" height="7" rx="1.2" fill={GOLD} />
                <rect x="10" y="1" width="7" height="7" rx="1.2" fill={GOLD} />
                <rect x="1" y="10" width="7" height="7" rx="1.2" fill={GOLD} />
                <rect x="10" y="10" width="7" height="7" rx="1.2" fill={GOLD} />
              </svg>
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>QuickGrowth</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'capitalize' }}>{role?.replace('_', ' ')} Dashboard</p>
            </div>
          </div>

          {/* Search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8, padding: '7px 12px',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search apps…"
              autoFocus
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                fontSize: 13, color: 'rgba(255,255,255,0.8)', caretColor: GOLD,
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 12 }}>✕</button>
            )}
          </div>
        </div>

        {/* Pinned Apps */}
        <div style={{ padding: '12px 16px' }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', marginBottom: 10 }}>
            {search ? 'Results' : 'Pinned Apps'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
            {(search ? filtered : apps.slice(0, 8)).map(app => (
              <button
                key={app.id}
                className="sm-app-btn"
                onClick={() => go(app.route)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 5, padding: '10px 6px', borderRadius: 10,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  transition: 'background 150ms ease',
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20,
                }}>
                  {app.icon}
                </div>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', textAlign: 'center', lineHeight: 1.3 }}>{app.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Items */}
        {!search && recentFormatted.length > 0 && (
          <div style={{ padding: '8px 16px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', marginBottom: 8 }}>
              Recently Opened
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentFormatted.map(item => (
                <div
                  key={item.id}
                  className="sm-recent-item"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px',
                    borderRadius: 8, background: 'transparent', cursor: 'pointer',
                    transition: 'background 150ms ease',
                  }}
                  onClick={() => { navigate(item.route || '/dashboard/sales/pipeline'); onClose(); }}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon || '📄'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</p>
                    {item.subtitle && <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.subtitle}</p>}
                  </div>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>{item.type}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>QuickGrowth Platform</p>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { icon: '⚙️', label: 'Settings', route: '/dashboard/sales/profile' },
              { icon: '🔔', label: 'Notifications', route: '/dashboard/sales/notifications' },
            ].filter(Boolean).map(a => (
              <button key={a.icon} onClick={() => go(a.route)}
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', fontSize: 14, transition: 'all 150ms ease' }}
                title={a.label}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
              >
                {a.icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Taskbar({ role }) {
  const { items, restoreItem, removeItem, recentItems } = useTaskbar();
  useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [startOpen, setStartOpen] = useState(false);

  const ctx = PAGE_CONTEXTS[location.pathname] || null;

  const handleRestore = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    restoreItem(id);
    if (item.route && location.pathname !== item.route) {
      navigate(item.route);
    }
  };

  return (
    <>
      {startOpen && (
        <StartMenu
          onClose={() => setStartOpen(false)}
          role={role}
          recentItems={recentItems}
        />
      )}

      <div
        id="qg-taskbar"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: 52, zIndex: 40,
          background: 'rgba(10,10,18,0.9)',
          backdropFilter: 'blur(24px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center',
          padding: '0 6px',
          gap: 3,
          userSelect: 'none',
        }}
      >
        <style>{`
          @media (max-width: 600px) {
            #qg-taskbar .tb-context-label { display: none !important; }
          }
          @media (min-width: 1024px) {
            #qg-taskbar { left: 260px !important; }
          }
        `}</style>

        {/* ── Start button ── */}
        <StartButton active={startOpen} onClick={() => setStartOpen(s => !s)} />

        {/* ── Divider ── */}
        <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.08)', margin: '0 3px', flexShrink: 0 }} />

        {/* ── Context badge ── */}
        {ctx && (
          <div
            className="tb-context-label"
            style={{
              display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
              padding: '4px 10px', borderRadius: 8,
              background: ctx.accent, border: `1px solid ${ctx.border}`,
            }}
          >
            <span style={{ fontSize: 13 }}>{ctx.icon}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{ctx.label}</span>
          </div>
        )}

        {/* ── Divider (if items exist) ── */}
        {items.length > 0 && (
          <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.08)', margin: '0 3px', flexShrink: 0 }} />
        )}

        {/* ── Minimized windows ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          flex: 1, overflowX: 'auto', overflowY: 'hidden',
          scrollbarWidth: 'none',
        }}>
          {items.map(item => (
            <TaskbarWindowItem
              key={item.id}
              item={item}
              onRestore={handleRestore}
              onClose={removeItem}
            />
          ))}
          {items.length === 0 && !ctx && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', paddingLeft: 4 }}>No open windows</span>
          )}
        </div>

        {/* ── Right: clock + calendar ── */}
        <TaskbarClock />
      </div>
    </>
  );
}
